import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Ear, Users, BookOpen, Mic } from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import SEOHead from '../components/shared/SEOHead';

const services = [
  {
    icon: Ear,
    title: 'Culture assessment and listening',
    when: 'When leaders want to understand what employees are experiencing, where culture is helping and where it may be creating pressure.',
    what: 'Kalobrate gathers evidence through surveys, confidential listening sessions, leadership conversations and existing workplace data. The aim is to build a clear picture of how the organisation works and where the gaps are.',
    receives: 'A written summary of findings, identified priorities, practical recommendations and a leadership discussion to agree next steps.',
    leads: 'Clearer decisions about what to protect, what to change and where to focus resources.',
  },
  {
    icon: Users,
    title: 'Leadership and action planning',
    when: 'When leaders have findings or concerns but are unsure how to respond, or when they need support turning evidence into practical action.',
    what: 'Kalobrate works with leaders to interpret findings, set priorities and agree practical next steps. This may include facilitated discussions, action-planning sessions or ongoing guidance.',
    receives: 'Agreed priorities, a practical plan and ongoing support to help leaders make decisions with confidence.',
    leads: 'More consistent leadership responses, clearer communication and stronger follow-through on agreed actions.',
  },
  {
    icon: BookOpen,
    title: 'Manager and workforce support',
    when: 'When managers need to improve how they handle difficult conversations, or when the workforce would benefit from practical learning on topics like mental health, stress, resilience or communication.',
    what: 'Kalobrate delivers targeted sessions designed around the people, pressures and working patterns of the organisation. Sessions can reach different shifts, roles and levels of the business.',
    receives: 'Practical workshops that give managers and employees useful knowledge and actions they can apply in their own working lives.',
    leads: 'Greater confidence in handling difficult situations, improved team communication and stronger workplace support.',
  },
  {
    icon: Mic,
    title: 'Speaking, training and facilitated sessions',
    when: 'When an organisation, conference or sector body needs a practical, evidence-led talk on workplace culture, mental health or employee engagement in operational settings.',
    what: 'Kalobrate delivers talks, roundtable discussions and facilitated sessions that connect workplace health, culture and business performance. Content is designed around the audience and the sector.',
    receives: 'An engaging session that gives attendees practical ideas, clearer understanding and a reason to take action.',
    leads: 'Greater awareness among leaders and decision-makers, with practical actions they can consider within their own organisations.',
  },
];

const ServicesPage = () => (
  <PageLayout>
    <SEOHead
      title="Services | Kalobrate"
      description="Kalobrate provides culture assessment, leadership support, workforce learning and practical speaking sessions for mid-sized organisations in operational sectors."
      canonical="/services"
    />

    <section className="bg-carbon py-20 md:py-28 relative overflow-hidden">
      <img src="/assets/kalobrate-icon-outline-yellow.png" alt="" aria-hidden="true" className="absolute top-[-10%] right-[-5%] w-[55%] h-[120%] object-contain opacity-[0.06] pointer-events-none select-none" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight tracking-tight mb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          Support built around the <span className="yellow-highlight text-carbon">problem</span>
        </motion.h1>
        <motion.p className="text-base text-white/80 leading-relaxed" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          The work starts with understanding. What Kalobrate provides depends on what the organisation needs, not a fixed menu of products.
        </motion.p>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-16">
          {services.map((svc, i) => (
            <motion.div
              key={svc.title}
              className="border-b border-gray-100 pb-16 last:border-0"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-golden flex items-center justify-center flex-shrink-0">
                  <svc.icon className="w-5 h-5 text-carbon" />
                </div>
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-carbon tracking-tight">{svc.title}</h2>
              </div>

              <div className="ml-14 space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-carbon/50 uppercase tracking-wide mb-2">When it may be useful</h3>
                  <p className="text-sm text-carbon/80 leading-relaxed">{svc.when}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-carbon/50 uppercase tracking-wide mb-2">What Kalobrate does</h3>
                  <p className="text-sm text-carbon/80 leading-relaxed">{svc.what}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-carbon/50 uppercase tracking-wide mb-2">What the client receives</h3>
                  <p className="text-sm text-carbon/80 leading-relaxed">{svc.receives}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-carbon/50 uppercase tracking-wide mb-2">What the work may lead to</h3>
                  <p className="text-sm text-carbon/80 leading-relaxed">{svc.leads}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-golden py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-carbon mb-4 tracking-tight">Not sure which support fits?</h2>
        <p className="text-base text-carbon/80 mb-8 max-w-xl mx-auto leading-relaxed">A first conversation will help clarify what the organisation needs and where Kalobrate can be most useful.</p>
        <Link to="/contact" className="bg-carbon text-white px-7 py-3.5 font-semibold text-sm hover:bg-carbon/90 transition-colors inline-flex items-center">
          Talk to Ollie <ArrowRight className="ml-2 w-4 h-4" />
        </Link>
      </div>
    </section>
  </PageLayout>
);

export default ServicesPage;
